/**
 * Button Component for Poker Reborn
 *
 * A stylish, interactive button with hover/press states.
 * Base component used throughout the application.
 *
 * @see docs/screens/room/popups/README.md - Popup button patterns
 * @see docs/screens/lobby/README.md - Lobby button usage
 */

import { Container, Graphics, Text, FederatedPointerEvent } from 'pixi.js';
import { audioService } from '@/services/AudioService';

// =============================================================================
// TYPES
// =============================================================================

export interface ButtonStyle {
  /** Background color */
  backgroundColor: number;

  /** Background color on hover */
  hoverColor: number;

  /** Background color when pressed */
  pressColor: number;

  /** Background color when disabled */
  disabledColor: number;

  /** Text color */
  textColor: number;

  /** Border color */
  borderColor: number;

  /** Border width */
  borderWidth: number;

  /** Corner radius */
  cornerRadius: number;

  /** Font size */
  fontSize: number;

  /** Font family */
  fontFamily: string;

  /** Padding horizontal */
  paddingX: number;

  /** Padding vertical */
  paddingY: number;

  /** Glow color on hover (optional) */
  hoverGlowColor?: number;

  /** Glow size on hover */
  hoverGlowSize?: number;
}

export interface ButtonOptions {
  /** Button label */
  label: string;

  /** Button width (auto if not specified) */
  width?: number;

  /** Button height (auto if not specified) */
  height?: number;

  /** Custom style overrides */
  style?: Partial<ButtonStyle>;

  /** Initially disabled */
  disabled?: boolean;

  /** Play sound on click */
  playSound?: boolean;
}

// =============================================================================
// DEFAULT STYLE
// =============================================================================

const DEFAULT_STYLE: ButtonStyle = {
  backgroundColor: 0x2d5a27, // Poker green
  hoverColor: 0x3d7a37,
  pressColor: 0x1d4a17,
  disabledColor: 0x555555,
  textColor: 0xffffff,
  borderColor: 0x4a8a44,
  borderWidth: 2,
  cornerRadius: 8,
  fontSize: 18,
  fontFamily: 'Roboto, Arial, sans-serif',
  paddingX: 24,
  paddingY: 12,
};

// =============================================================================
// BUTTON COMPONENT
// =============================================================================

export class Button extends Container {
  private glowGraphics: Graphics;
  private background: Graphics;
  private labelText: Text;
  private style: ButtonStyle;

  private _disabled = false;
  private _pressed = false;
  private _hovered = false;

  private _width: number;
  private _height: number;

  public onClick?: () => void;
  private playSound: boolean;

  constructor(options: ButtonOptions) {
    super();

    this.style = { ...DEFAULT_STYLE, ...options.style };
    this.playSound = options.playSound ?? true;

    // Create glow (behind background)
    this.glowGraphics = new Graphics();
    this.glowGraphics.visible = false;
    this.addChild(this.glowGraphics);

    // Create background
    this.background = new Graphics();
    this.addChild(this.background);

    // Create label
    this.labelText = new Text({
      text: options.label,
      style: {
        fill: this.style.textColor,
        fontSize: this.style.fontSize,
        fontFamily: this.style.fontFamily,
        fontWeight: 'bold',
      },
    });
    this.labelText.anchor.set(0.5);
    this.addChild(this.labelText);

    // Calculate dimensions
    if (options.width && options.height) {
      this._width = options.width;
      this._height = options.height;
    } else {
      this._width = options.width ?? this.labelText.width + this.style.paddingX * 2;
      this._height = options.height ?? this.labelText.height + this.style.paddingY * 2;
    }

    // Position label
    this.labelText.x = this._width / 2;
    this.labelText.y = this._height / 2;

    // Initial render
    this.render();

    // Set up interactivity
    this.eventMode = 'static';
    this.cursor = 'pointer';

    this.on('pointerover', this.onPointerOver, this);
    this.on('pointerout', this.onPointerOut, this);
    this.on('pointerdown', this.onPointerDown, this);
    this.on('pointerup', this.onPointerUp, this);
    this.on('pointerupoutside', this.onPointerUpOutside, this);

    // Set disabled state
    if (options.disabled) {
      this.disabled = true;
    }
  }

  // ===========================================================================
  // PUBLIC API
  // ===========================================================================

  /** Get/set button label */
  get label(): string {
    return this.labelText.text;
  }

  set label(value: string) {
    this.labelText.text = value;
  }

  /** Get/set disabled state */
  get disabled(): boolean {
    return this._disabled;
  }

  set disabled(value: boolean) {
    this._disabled = value;
    this.cursor = value ? 'default' : 'pointer';
    this.render();
  }

  /** Get button width */
  get buttonWidth(): number {
    return this._width;
  }

  /** Get button height */
  get buttonHeight(): number {
    return this._height;
  }

  /** Update button style */
  setStyle(style: Partial<ButtonStyle>): void {
    this.style = { ...this.style, ...style };
    this.render();
  }

  /** Resize button */
  resize(width: number, height: number): void {
    this._width = width;
    this._height = height;
    this.labelText.x = width / 2;
    this.labelText.y = height / 2;
    this.render();
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  private render(): void {
    const { style } = this;

    // Determine background color
    let bgColor = style.backgroundColor;
    if (this._disabled) {
      bgColor = style.disabledColor;
    } else if (this._pressed) {
      bgColor = style.pressColor;
    } else if (this._hovered) {
      bgColor = style.hoverColor;
    }

    // Draw hover glow if enabled
    if (style.hoverGlowColor !== undefined && this._hovered && !this._disabled) {
      const glowSize = style.hoverGlowSize ?? 8;
      this.glowGraphics.clear();
      // Draw multiple layers for soft glow
      for (let i = 3; i >= 0; i--) {
        const expand = glowSize * (i / 3);
        const alpha = 0.15 * (3 - i);
        this.glowGraphics.roundRect(
          -expand,
          -expand,
          this._width + expand * 2,
          this._height + expand * 2,
          style.cornerRadius + expand
        );
        this.glowGraphics.fill({ color: style.hoverGlowColor, alpha });
      }
      this.glowGraphics.visible = true;
    } else {
      this.glowGraphics.visible = false;
    }

    // Draw background
    this.background.clear();
    this.background.roundRect(0, 0, this._width, this._height, style.cornerRadius);
    this.background.fill(bgColor);

    // Draw border
    if (style.borderWidth > 0) {
      this.background.roundRect(0, 0, this._width, this._height, style.cornerRadius);
      this.background.stroke({ width: style.borderWidth, color: style.borderColor });
    }

    // Update text color
    this.labelText.style.fill = this._disabled ? 0x888888 : style.textColor;
  }

  private onPointerOver(_event: FederatedPointerEvent): void {
    if (this._disabled) return;
    this._hovered = true;
    this.render();
  }

  private onPointerOut(_event: FederatedPointerEvent): void {
    if (this._disabled) return;
    this._hovered = false;
    this._pressed = false;
    this.render();
  }

  private onPointerDown(_event: FederatedPointerEvent): void {
    if (this._disabled) return;
    this._pressed = true;
    this.render();
  }

  private onPointerUp(_event: FederatedPointerEvent): void {
    if (this._disabled) return;

    if (this._pressed) {
      this._pressed = false;
      this.render();

      // Play click sound
      if (this.playSound) {
        audioService.play('button_click');
      }

      // Trigger callback
      this.onClick?.();
    }
  }

  private onPointerUpOutside(_event: FederatedPointerEvent): void {
    this._pressed = false;
    this.render();
  }
}

// =============================================================================
// PRESET BUTTON STYLES
// =============================================================================

/** Primary action button (green) */
export const PRIMARY_BUTTON_STYLE: Partial<ButtonStyle> = {
  backgroundColor: 0x2d8a27,
  hoverColor: 0x3daa37,
  pressColor: 0x1d6a17,
  borderColor: 0x4aba44,
};

/** Secondary button (blue) */
export const SECONDARY_BUTTON_STYLE: Partial<ButtonStyle> = {
  backgroundColor: 0x2757a2,
  hoverColor: 0x3777c2,
  pressColor: 0x174792,
  borderColor: 0x4787b2,
};

/** Danger button (red) */
export const DANGER_BUTTON_STYLE: Partial<ButtonStyle> = {
  backgroundColor: 0xa22727,
  hoverColor: 0xc23737,
  pressColor: 0x821717,
  borderColor: 0xb24747,
};

/** Gold button (for special actions) */
export const GOLD_BUTTON_STYLE: Partial<ButtonStyle> = {
  backgroundColor: 0xb8860b,
  hoverColor: 0xd8a62b,
  pressColor: 0x986600,
  borderColor: 0xffd700,
  textColor: 0x000000,
  hoverGlowColor: 0xffd700,
  hoverGlowSize: 10,
};
